import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute } from '@angular/router';
import { ElementRef, Optional, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../view-context';
import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, Input, OnChanges, Output, SimpleChanges,
} from '@angular/core';
import { BrIconComponent } from '../icon/icon.component';
import { BrPopoverTriggerDirective } from '../popover/popover-trigger.directive';
import { BrMenuComponent } from '../menu/menu.component';
import { BrMenuItemComponent } from '../menu/menu-item.component';
import { BrMenuTriggerDirective } from '../menu/menu-trigger.directive';
import { MatTooltipModule } from '@angular/material/tooltip';

export type DataFilterValue = string | number | boolean | null;

/**
 * What kind of value a column holds, which is what decides the comparisons it
 * can be asked for and therefore the editor its dropdown shows.
 *
 * These four and their operator ids mirror the server's own whitelist
 * (WorkItemFilterKinds / WorkItemFilterOps). They are sent verbatim, so a name
 * invented here would be a 400 rather than a filter.
 */
export type DataFilterKind = 'choice' | 'text' | 'number' | 'date';

/** One comparison a rule may ask for: the id goes on the wire, the label on screen. */
export interface DataFilterOp { id: string; label: string; }

/**
 * The comparisons each kind offers, in the order the dropdown lists them. The
 * first is the default, so it is the one a rule means when nobody chose.
 */
export const DATA_FILTER_OPS: Readonly<Record<DataFilterKind, readonly DataFilterOp[]>> = {
  choice: [{ id: 'eq', label: 'is' }],
  text: [{ id: 'contains', label: 'contains' }, { id: 'eq', label: 'is' }],
  number: [
    { id: 'gte', label: 'at least' },
    { id: 'lte', label: 'at most' },
    { id: 'eq', label: 'exactly' },
  ],
  date: [{ id: 'after', label: 'on or after' }, { id: 'before', label: 'on or before' }],
};

/**
 * A rule whose comparison is part of the question.
 *
 * A choice column stays a bare value: "is" is the only thing it can mean, and
 * wrapping it would make every existing caller carry an operator it never
 * chose.
 */
export interface DataFilterRule { op: string; value: string | number; }

export const isRule = (v: unknown): v is DataFilterRule =>
  typeof v === 'object' && v !== null && 'op' in v;

export interface DataFilterOption {
  value: Exclude<DataFilterValue, null>;
  label: string;
}

/** One select filter owned by the page and rendered by the shared filter bar. */
export interface DataFilterField {
  id: string;
  label: string;
  allLabel?: string;
  /** Required for a choice column; ignored by every other kind. */
  options: readonly DataFilterOption[];
  /** Defaults to 'choice', which is what every filter was before kinds existed. */
  kind?: DataFilterKind;
  /**
   * Whether this filter earns permanent space on the bar.
   *
   * A pinned filter is always shown and can only be deselected — the rule goes
   * back to "all", but the control stays, ready to be picked again. An unpinned
   * one lives in the More-filters panel; it appears on the bar as a chip only
   * while it is filtering, and its × takes it off again by clearing it.
   *
   * Defaults to pinned, so a page that declares no panel filters keeps showing
   * every filter it has, and shows no Filters button at all.
   */
  pinned?: boolean;
}

export type DataFilterValues =
  Readonly<Record<string, DataFilterValue | DataFilterRule>>;

/**
 * Shared filter bar for server-backed lists.
 *
 * A page describes its business filters and decides when the current values
 * should be applied. Control changes emit `change`; calling `apply()` emits the
 * current value object through `applied`.
 *
 * Two halves, and they behave differently on purpose. The BAR holds the
 * filters a page pins, one control each, and each asks its question as soon as
 * it is answered. The PANEL behind the Filters button holds every other column
 * the page declares, and batches: it stages edits and asks once, on Apply.
 * That difference is not a style - one dropdown answered is one query, and a
 * dozen controls answered one at a time would be a dozen.
 */
@Component({
  selector: 'app-data-filter',
  standalone: true,
  imports: [CommonModule, BrIconComponent, BrMenuComponent, BrMenuItemComponent, BrMenuTriggerDirective, BrPopoverTriggerDirective, MatTooltipModule],
  templateUrl: './data-filter.component.html',
  styleUrls: ['./data-filter.component.css'],
})
export class DataFilterComponent implements OnChanges, OnInit, OnDestroy {
  @Input() contextKey = "";
  @Input() fields: readonly DataFilterField[] = [];
  @Input() values: DataFilterValues = {};
  @Output() change = new EventEmitter<DataFilterValues>();
  @Output() applied = new EventEmitter<DataFilterValues>();

  private contextRegistration?: Subscription;
  constructor(
    @Optional() private readonly context?: AppContextService,
    @Optional() private readonly contextRoute?: ActivatedRoute,
    @Optional() private readonly contextElement?: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.contextRegistration = this.context?.registerView({
      key: viewContextKey(this.contextRoute, `filters:${this.contextKey}`),
      name: 'Applied filters', priority: 0,
      active: () => viewContextVisible(this.contextElement),
      read: () => this.values,
      describe: values => JSON.stringify(values),
      restore: values => {
        this.values = values;
        this.currentValues = { ...values };
        // Use the page's existing application path once. An immediate filter
        // generally handles change by calling apply(); explicit ones use applied.
        if (this.change.observed) this.change.emit({ ...values });
        else this.applied.emit({ ...values });
      },
    });
  }

  ngOnDestroy(): void { this.contextRegistration?.unsubscribe(); }

  private currentValues: Record<string, DataFilterValue | DataFilterRule> = {};
  /**
   * A comparison chosen before there was anything to compare against.
   *
   * Without this, picking "at most" on an empty box would be forgotten the
   * moment it was read back from the (still absent) rule, and the button would
   * spring back to "at least" under the pointer.
   */
  private readonly pendingOps: Record<string, string> = {};

  /**
   * What the More-filters panel is holding, while it is open.
   *
   * The panel BATCHES. Every control on the bar itself asks its question the
   * moment it is answered, which is right for one dropdown - but a panel of a
   * dozen filters that reloaded a database-paged table on every click would
   * fire a query per keystroke and per checkbox, and each one would move the
   * rows under the hand still choosing. So edits land here, and Apply is what
   * asks. Closing without applying throws the draft away, which is what makes
   * the panel safe to open and read.
   *
   * Null when the panel is shut, so nothing can be staged from nowhere.
   */
  private draft: Record<string, DataFilterValue | DataFilterRule> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['values']) {
      this.currentValues = this.valuesForCurrentFields(this.values);
      return;
    }

    if (changes['fields']) {
      this.currentValues = this.valuesForCurrentFields(this.currentValues);
    }
  }

  /** The bare value, for a choice column. A rule reads as its own value. */
  valueFor(fieldId: string): DataFilterValue {
    const held = this.currentValues[fieldId] ?? null;
    return isRule(held) ? held.value : held;
  }

  kindOf(field: DataFilterField): DataFilterKind {
    return field.kind ?? 'choice';
  }

  opsFor(field: DataFilterField): readonly DataFilterOp[] {
    return DATA_FILTER_OPS[this.kindOf(field)];
  }

  /**
   * The comparison currently in force. Falls back to the kind's first, which
   * is what an unanswered rule already means - so the dropdown opens showing
   * the same comparison the query would use.
   */
  opFor(field: DataFilterField): string {
    const held = this.currentValues[field.id];
    if (isRule(held)) return held.op;
    return this.pendingOps[field.id] ?? this.opsFor(field)[0].id;
  }

  /**
   * Changes the comparison, keeping whatever value is already there. With no
   * value yet it is only remembered - there is no rule to re-ask.
   */
  setOp(field: DataFilterField, op: string): void {
    this.pendingOps[field.id] = op;
    const value = this.valueFor(field.id);
    if (value !== null) this.setRule(field, op, String(value));
  }

  /** What goes in the text/number/date box: never null, so the input binds. */
  textFor(field: DataFilterField): string {
    const value = this.valueFor(field.id);
    return value === null ? '' : String(value);
  }

  /**
   * Sets one side of a rule and keeps the other.
   *
   * An empty box clears the rule rather than filtering on the empty string:
   * someone deleting what they typed is widening the query, not asking for
   * the rows whose title is nothing.
   */
  setRule(field: DataFilterField, op: string, value: string): void {
    const text = value.trim();
    if (!text) { this.setValue(field.id, null); return; }
    this.currentValues = {
      ...this.currentValues,
      [field.id]: {
        op,
        value: this.kindOf(field) === 'number' ? Number(text) : text,
      },
    };
    this.change.emit(this.currentValueObject());
  }

  /**
   * What the control reads once a value is picked: the rule, not just the
   * answer. "Prospect" alone leaves the reader to work out which column is
   * doing the filtering, which is the whole question when several rules are
   * on the bar at once.
   */
  labelFor(field: DataFilterField): string {
    const value = this.valueFor(field.id);
    if (value === null) return field.label;
    if (this.kindOf(field) === 'choice') {
      const option = field.options.find(item => item.value === value);
      return option ? `${field.label}: ${option.label}` : field.label;
    }

    // "Score at least 60" - the column, the comparison and the value, because
    // with several rules up none of the three is recoverable from the others.
    const op = this.opsFor(field).find(item => item.id === this.opFor(field));
    return `${field.label} ${op?.label ?? ''} ${value}`.replace(/\s+/g, ' ').trim();
  }

  /** Pinned filters, plus any unpinned one that is currently filtering. */
  get visibleFields(): readonly DataFilterField[] {
    return this.fields.filter(field => this.isVisible(field));
  }

  /**
   * What the More-filters panel holds: every unpinned column, always.
   *
   * This replaced a "+" menu that added ONE column to the bar at a time. The
   * plus sign told somebody who had not used this bar nothing - not that the
   * bar filters, not that there were columns behind it - and finding a filter
   * meant opening a menu, picking a name, and only then being shown the
   * control. Three steps to reach a question you could not see you could ask.
   *
   * A panel shows every column it has at once, which is the whole point: you
   * read what can be narrowed instead of guessing what to look for. An
   * unpinned filter that IS set still gets its own chip on the bar, so the
   * panel is where you go to change filters, not the only place they exist.
   */
  get panelFields(): readonly DataFilterField[] {
    return this.fields.filter(field => field.pinned === false);
  }

  /**
   * Reads a select's string back to the option's own value.
   *
   * A select only ever reports strings, and these values are typed - a rep is
   * the number 7, not "7". Matching on the rendered string and handing back
   * the ORIGINAL value is what keeps `assignee:eq:7` a number all the way to
   * the query. An empty string is the "All" row, which is the absence of a
   * filter rather than a filter on nothing.
   */
  setPanelChoice(field: DataFilterField, raw: string): void {
    const option = field.options.find(item => String(item.value) === raw);
    this.setPanelValue(field.id, option ? option.value : null);
  }

  /** How many of the panel's columns are filtering, for the button's badge. */
  get panelActiveCount(): number {
    return this.panelFields.filter(field => this.valueFor(field.id) !== null).length;
  }

  isRemovable(field: DataFilterField): boolean {
    return field.pinned === false;
  }

  // ── The More-filters panel ─────────────────────────────────────────────

  /** Takes a copy to edit. Opening the panel changes nothing by itself. */
  openPanel(): void {
    this.draft = { ...this.currentValues };
  }

  /** Discards whatever was staged. Called when the panel closes by any route
   *  other than Apply - Escape, a click outside, the button again. */
  closePanel(): void {
    this.draft = null;
  }

  /** The staged value, or the live one before anything is staged. */
  panelValueFor(fieldId: string): DataFilterValue {
    const held = (this.draft ?? this.currentValues)[fieldId] ?? null;
    return isRule(held) ? held.value : held;
  }

  panelOpFor(field: DataFilterField): string {
    const held = (this.draft ?? this.currentValues)[field.id];
    if (isRule(held)) return held.op;
    return this.pendingOps[field.id] ?? this.opsFor(field)[0].id;
  }

  panelTextFor(field: DataFilterField): string {
    const value = this.panelValueFor(field.id);
    return value === null ? '' : String(value);
  }

  panelHasValue(fieldId: string): boolean {
    return this.panelValueFor(fieldId) !== null;
  }

  setPanelValue(fieldId: string, value: DataFilterValue | DataFilterRule): void {
    this.draft = { ...(this.draft ?? this.currentValues), [fieldId]: value };
  }

  /** Changes the comparison, keeping whatever value is staged. */
  setPanelOp(field: DataFilterField, op: string): void {
    this.pendingOps[field.id] = op;
    const value = this.panelValueFor(field.id);
    if (value !== null) this.setPanelRule(field, op, String(value));
  }

  setPanelRule(field: DataFilterField, op: string, value: string): void {
    const text = value.trim();
    if (!text) { this.setPanelValue(field.id, null); return; }
    this.setPanelValue(field.id, {
      op,
      value: this.kindOf(field) === 'number' ? Number(text) : text,
    });
  }

  /**
   * Clears EVERY filter, the pinned ones on the bar included.
   *
   * "Reset all filters" is what the control says, so all is what it clears -
   * a reset that left a pinned Territory still narrowing the rows would be the
   * more surprising of the two readings, and it is the one somebody reaches
   * for when the result is empty and they cannot see why.
   *
   * Staged like every other panel edit: it takes effect on Apply, so it can be
   * taken back by closing the panel.
   */
  resetAll(): void {
    this.draft = Object.fromEntries(this.fields.map(field => [field.id, null]));
  }

  /** Whether Reset has anything to undo, so it can be disabled rather than
   *  offering an action that would do nothing. */
  get panelHasAnyValue(): boolean {
    const source = this.draft ?? this.currentValues;
    return this.fields.some(field => {
      const held = source[field.id] ?? null;
      return (isRule(held) ? held.value : held) !== null;
    });
  }

  /**
   * Commits the draft and asks the question.
   *
   * Emits BOTH events. Pages listen to one or the other - the Leads board
   * reloads on `change`, the table pages call apply() from `change` and read
   * `applied` - and a panel that emitted only one of them would work on some
   * of its callers and silently do nothing on the rest.
   */
  applyPanel(): void {
    if (this.draft) this.currentValues = this.draft;
    this.draft = null;
    const values = this.currentValueObject();
    this.change.emit(values);
    this.applied.emit(values);
  }

  /**
   * Takes an unpinned filter off the bar. The value has to go with it —
   * a rule that is no longer shown must not still be narrowing the rows.
   *
   * That is now the only thing that puts it back in the panel and out of the
   * bar: an unpinned filter is on the bar exactly while it has a value.
   */
  remove(fieldId: string): void {
    this.setValue(fieldId, null);
  }

  private isVisible(field: DataFilterField): boolean {
    return field.pinned !== false || this.valueFor(field.id) !== null;
  }

  hasValue(fieldId: string): boolean {
    return this.valueFor(fieldId) !== null;
  }

  setValue(fieldId: string, value: DataFilterValue): void {
    this.currentValues = { ...this.currentValues, [fieldId]: value };
    this.change.emit(this.currentValueObject());
  }

  apply(): void {
    this.applied.emit(this.currentValueObject());
  }

  clear(): void {
    this.currentValues = Object.fromEntries(
      this.fields.map(field => [field.id, null]));
    this.change.emit(this.currentValueObject());
  }

  trackField(_: number, field: DataFilterField): string {
    return field.id;
  }

  trackOption(_: number, option: DataFilterOption): DataFilterValue {
    return option.value;
  }

  private valuesForCurrentFields(
    source: DataFilterValues,
  ): Record<string, DataFilterValue | DataFilterRule> {
    return Object.fromEntries(this.fields.map(field => [
      field.id,
      source[field.id] ?? null,
    ]));
  }

  private currentValueObject(): DataFilterValues {
    return { ...this.valuesForCurrentFields(this.currentValues) };
  }
}
